import { CommonModule } from '@angular/common';
import { Component, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import type { BusinessUnitManagementModel } from '../../../models';

/**
 * BusinessUnitSidebarComponent
 *
 * Sidebar navigation for business units with search/filter functionality.
 * Displays a searchable list of business units with member counts.
 */
@Component({
  selector: 'dtis-admin-business-unit-sidebar',
  imports: [CommonModule, FormsModule, RouterModule, MatListModule, MatIconModule, MatButtonModule, TranslocoModule],
  template: `
    <div class="flex h-full flex-col border-r border-outlineVariant bg-surface gap-1 ">
      <!-- Header -->
      <div class="px-4 py-4">
        <div class="flex items-baseline gap-4">
          <h2 class="text-title-small">
            {{ 'business-unit-management' | transloco }}
          </h2>
          <span class="text-label-small text-onSurfaceVariant">
            {{ businessUnits().length }}
          </span>
        </div>
      </div>
      <div class="px-4 py-4">
        <!-- Add Button -->
        <button
          matButton="filled"
          color="primary"
          class="w-full"
          (click)="createNew.emit()"
        >
          {{ 'add-business-unit' | transloco }}
        </button>
      </div>
      <hr class="bg-outlineVariant h-[1px]  "/>
      <div class="px-4 py-4">

    <!-- Search -->
        <div class="flex w-full items-center ">
          <input
            class="border-outlineVariant text-onSurfaceVariant focus:border-primary h-min-touch w-full rounded-full border p-2 px-4 focus:outline-none focus:ring-0"
            #searchElement
            type="text"
            (input)="searchTerm.set($event.target.value)"
            placeholder="{{ 'search-business-units' | transloco }}"
          />
          @if (searchTerm()) {
            <button
              mat-icon-button
              class="-ml-10!"
              (click)="searchTerm.set('')"
            >
              <mat-icon class="text-onSurfaceVariant">close</mat-icon>
            </button>
          }
        </div>
        </div>
      <hr class="bg-outlineVariant h-[1px]  "/>
      <!-- Business Units List -->
      <mat-nav-list class="flex-1 overflow-y-auto">
        @for (unit of filteredUnits(); track unit.identifier) {
          <a
            mat-list-item
            [routerLink]="['/business-unit-management', unit.identifier]"
            routerLinkActive="!bg-primaryContainer"
            class="cursor-pointer hover:bg-primary/1 h-[72px]!"
          >
            <div class="flex w-full items-center gap-3">
              <div class="rounded-full h-10! w-10! flex justify-center bg-primary items-center">
                <mat-icon class="text-white!">business</mat-icon>
              </div>
              <div class="flex-1">
                <div class="text-body-large font-medium">{{ unit.name }}</div>
              </div>
              <mat-icon class="text-onSurfaceVariant">chevron_right</mat-icon>
            </div>
          </a>
        } @empty {
          <div class="p-4 text-center text-onSurfaceVariant">
            @if (searchTerm()) {
              {{ 'no-results-found' | transloco }}
            } @else {
              {{ 'no-business-units' | transloco }}
            }
          </div>
        }
      </mat-nav-list>
    </div>
  `,
  host: { class: 'h-full block w-80 ' },
  styles: [
    `
    @reference "tailwindcss";
    mat-list-item {
      @apply transition-colors duration-200;
    }
  `,
  ],
})
export class BusinessUnitSidebarComponent {
  /**
   * List of all business units
   */
  businessUnits = input.required<BusinessUnitManagementModel[]>();

  /**
   * Currently selected business unit
   */
  selectedUnit = input<BusinessUnitManagementModel>();

  /**
   * Emitted when a business unit is selected
   */
  selectUnit = output<BusinessUnitManagementModel>();

  /**
   * Emitted when create new button is clicked
   */
  createNew = output<void>();

  /**
   * Search term for filtering business units
   */
  searchTerm = signal<string>('');

  /**
   * Filtered list of business units based on search term
   */
  filteredUnits = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    if (!term) {
      return this.businessUnits();
    }
    return this.businessUnits().filter((unit) => unit.name.toLowerCase().includes(term) || unit.description?.toLowerCase().includes(term));
  });

  /**
   * Gets the user count from membersCount
   */
  getUserCount(unit: BusinessUnitManagementModel): number {
    // Placeholder - returns membersCount minus admins, should be updated when API provides actual data
    return unit.membersCount;
  }
}
